import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Active UI mode (driver | host) — a VIEW concept, separate from the account
  // role/RBAC. Persisted to localStorage and synced to the backend `mode` field
  // via the existing POST /api/auth/mode. Every fresh login starts in driver mode.
  const [mode, setModeState] = useState(() => localStorage.getItem("mrp_mode") || "driver");

  const refresh = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      setModeState(localStorage.getItem("mrp_mode") || u.mode || "driver");
      return u;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-sync /auth/me when the tab regains focus (throttled 15s) so server-side
  // changes — e.g. an admin granting Commercial Mode — appear without a
  // manual reload or cold re-login.
  useEffect(() => {
    let last = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15000) return;
      last = now;
      api.me().then(setUser).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fresh logins always start in DRIVER mode (product requirement); the server
  // mode field is synced best-effort so the mobile app sees the same mode.
  const applyLoginMode = (u) => {
    localStorage.setItem("mrp_mode", "driver");
    setModeState("driver");
    if (u?.mode && u.mode !== "driver") api.setMode("driver").catch(() => {});
    return u ? { ...u, mode: "driver" } : u;
  };

  const switchMode = (m) => {
    if (m !== "driver" && m !== "host" && !(m === "commercial" && user?.commercial_access === "active")) return;
    localStorage.setItem("mrp_mode", m);
    setModeState(m);
    setUser((u) => (u ? { ...u, mode: m } : u));
    api.setMode(m).catch(() => {});
  };

  const login = async (email, password) => {
    const r = await api.login({ email, password });
    const u = applyLoginMode(r.user);
    setUser(u);
    return { user: u, show_reward: !!r.show_reward };
  };

  const register = async (email, password, name, referralCode) => {
    const r = await api.register({ email, password, name, ...(referralCode ? { referral_code: referralCode } : {}) });
    const u = applyLoginMode(r.user);
    setUser(u);
    return { user: u, fresh_signup: !!r.fresh_signup, show_reward: !!r.show_reward };
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* server session may already be gone */ }
    localStorage.removeItem("mrp_mode");
    setModeState("driver");
    setUser(null);
    // Hard navigation prevents back-button return into cached authed screens.
    window.location.replace("/login");
  };

  // Direct Google Identity Services flow: the GIS button hands us a signed
  // id_token, the backend verifies it against Google's JWKS and sets the cookie.
  const loginWithGoogle = async (credential) => {
    const r = await api.googleAuth(credential);
    const u = applyLoginMode(r.user);
    setUser(u);
    return { user: u, fresh_signup: !!r.fresh_signup, show_reward: !!r.show_reward };
  };

  const role = user?.role || (user?.is_admin ? "admin" : "driver");
  const isAdmin = role === "admin" || !!user?.is_admin;
  const isSupport = role === "support" || isAdmin;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, refresh, setUser, role, isAdmin, isSupport, mode, switchMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
